import { makeStyles } from '@material-ui/core/styles';
import { mergeClasses } from '@material-ui/styles';
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { ArrowV, BasicCalendar } from '~svg-components';
import { FORMAT_DATE_MONTH_YEAR, formatDateMonthYear } from '~utils/date-format';
import hexToRGBA from '~utils/hex-to-rgba';
import RangeDayPickerInput from './range-day-picker-input';

const useStyles = makeStyles((theme) => ({
  rangePicker: {
    marginRight: 4,
    position: 'relative',
    cursor: 'pointer',
    maxWidth: 256,
    backgroundColor: hexToRGBA(theme.colors.greyE8, 0.6),
    border: 'none',
    borderRadius: 20,
    '&:hover': {
      backgroundColor: `${theme.colors.greyE8} !important`,
    },
  },
  input: { padding: '4px 9px 4px 8px', cursor: 'pointer', ...theme.typography.body2 },
  focused: { boxShadow: 'none' },
  keyboardArrow: {
    marginRight: 16,
    color: theme.colors.grey66,
    transform: ({ open }) => (open ? 'rotate(270deg)' : 'rotate(90deg)'),
    fontSize: 10,
    transition: 'transform 150ms cubic-bezier(0.65, 0.05, 0.36, 1)',
  },
}));

const RangeDayPickerSelect = forwardRef((props, ref) => {
  const {
    onChange,
    value: propsValue,
    DayPickerProps,
    hasStartAdornment,
    formatFunction,
    dateFormat,
    isAutoColesWhenFinishSelected,
    endAdornmentIcon,
    ...othersProps
  } = props;
  const [open, setOpen] = useState(false);
  const classes = useStyles({ open });
  const [cacheDate, setCacheDate] = useState('');
  const { disabledDays, ...otherDayPickerProps } = DayPickerProps || {};
  const { classes: inputPropsClasses, ...otherInputProps } = othersProps.InputProps || {};
  return (
    <RangeDayPickerInput
      ref={ref}
      value={cacheDate || propsValue}
      onChange={(value) => {
        const { from, to } = value;
        if (from && !to) {
          return setCacheDate(formatFunction(from));
        }
        setCacheDate('');
        if (isAutoColesWhenFinishSelected) {
          setOpen(false);
        }
        return onChange(`${formatFunction(from)} - ${formatFunction(to)}`);
      }}
      format={dateFormat}
      startAdornment={
        hasStartAdornment && <BasicCalendar color="grey66" style={{ marginLeft: 16 }} />
      }
      endAdornment={endAdornmentIcon || <ArrowV className={classes.keyboardArrow} />}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        if (cacheDate) {
          setCacheDate('');
        }
        setOpen(false);
      }}
      DayPickerProps={{
        ...otherDayPickerProps,
        disabledDays: (() => {
          if (typeof disabledDays === 'function') {
            const [dateFrom] = (cacheDate || propsValue).split(' - ').map((item) => new Date(item));
            return disabledDays(dateFrom);
          }
          return disabledDays;
        })(),
      }}
      {...othersProps}
      InputProps={{
        classes: mergeClasses({
          baseClasses: {
            root: classes.rangePicker,
            input: classes.input,
            focused: classes.focused,
          },
          newClasses: inputPropsClasses,
        }),
        ...otherInputProps,
      }}
    />
  );
});

RangeDayPickerSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  DayPickerProps: PropTypes.object,
  dateFormat: PropTypes.string,
  formatFunction: PropTypes.func,
  hasStartAdornment: PropTypes.bool,
  isAutoColesWhenFinishSelected: PropTypes.bool,
  endAdornmentIcon: PropTypes.node,
};

RangeDayPickerSelect.defaultProps = {
  value: '',
  DayPickerProps: undefined,
  dateFormat: FORMAT_DATE_MONTH_YEAR,
  formatFunction: formatDateMonthYear,
  hasStartAdornment: true,
  isAutoColesWhenFinishSelected: false,
};

export default RangeDayPickerSelect;
